import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { Factory, Plus, RefreshCw, Loader2, Download, Upload } from 'lucide-react';
import { Modal } from './ui';
import {
  getAdminManufacturers,
  createAdminManufacturer,
  updateAdminManufacturer,
  setAdminManufacturerActive,
  deleteAdminManufacturer,
  bulkAdminManufacturers,
  exportAdminManufacturers,
  importAdminManufacturers,
  type ManufacturerItem,
} from '../api';
import {
  useCatalogSelection,
  useCatalogBulk,
  CatalogToolbar,
  BulkActionBar,
  BulkResultNotice,
  CatalogRowActions,
  filterCatalog,
  type CatalogStatusFilter,
} from './catalog';
import { extractApiErrorMessage } from '../utils/apiError';

export function AdminManufacturerCatalog() {
  const [manufacturers, setManufacturers] = useState<ManufacturerItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ManufacturerItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    created: number;
    skipped?: number;
    total: number;
    errors?: Array<{ row: number; message: string }>;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchManufacturers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getAdminManufacturers(false);
      if (response.status && response.data) {
        setManufacturers(response.data);
      }
    } catch {
      setError('Failed to load manufacturers');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchManufacturers();
  }, [fetchManufacturers]);

  // O(1) lookup of an existing manufacturer by case-insensitive trimmed name,
  // used by the modal to flag duplicates as the user types (HBAC2-8836). The
  // backend remains the authoritative gate; this is purely UX.
  const manufacturersByName = useMemo(() => {
    const map = new Map<string, ManufacturerItem>();
    for (const m of manufacturers) {
      map.set(m.name.trim().toLowerCase(), m);
    }
    return map;
  }, [manufacturers]);

  // handleCreate / handleUpdate are only invoked from ManufacturerFormModal's
  // handleSubmit, which already wraps the call in try/catch and renders the
  // server's message inline via setSubmitError. We intentionally do NOT catch
  // here — a local catch would either swallow the error (modal couldn't show
  // it) or be a bare re-throw. The page-level error banner is reserved for
  // non-modal flows (list load, export, deactivate). If these are ever called
  // from somewhere other than the modal, add error handling at the call site.
  const handleCreate = async (name: string) => {
    const response = await createAdminManufacturer({ name });
    if (response.status && response.data) {
      setManufacturers(prev =>
        [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name))
      );
      setShowCreateModal(false);
    }
  };

  const handleUpdate = async (manufacturerId: string, name: string) => {
    const response = await updateAdminManufacturer(manufacturerId, { name });
    if (response.status && response.data) {
      setManufacturers(prev =>
        prev
          .map(m => (m.manufacturerId === manufacturerId ? response.data : m))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setEditingItem(null);
    }
  };

  // ---- Selection, search / filter, bulk actions ----
  const selection = useCatalogSelection();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CatalogStatusFilter>('active'); // delisted items hidden by default
  const filtered = useMemo(
    () =>
      filterCatalog(
        manufacturers,
        search,
        statusFilter,
        item => [item.name],
        item => item.isActive !== false
      ),
    [manufacturers, search, statusFilter]
  );
  const visibleIds = useMemo(() => filtered.map(item => item.manufacturerId), [filtered]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every(id => selection.isSelected(id));

  // Drop selections for rows that disappeared after a refetch / delete
  const pruneSelection = selection.prune;
  useEffect(() => {
    pruneSelection(manufacturers.map(item => item.manufacturerId));
  }, [manufacturers, pruneSelection]);

  const bulk = useCatalogBulk<ManufacturerItem>({
    entityLabel: 'manufacturer',
    run: bulkAdminManufacturers,
    getId: item => item.manufacturerId,
    withActive: (item, isActive) => ({ ...item, isActive }),
    setItems: setManufacturers,
    selection,
    onError: setError,
  });

  const [rowBusyId, setRowBusyId] = useState<string | null>(null);

  /** Reversible: hides from the complaint form, keeps historical complaint links intact. */
  const handleToggleActive = async (item: ManufacturerItem) => {
    const next = !(item.isActive !== false);
    if (
      !next &&
      !window.confirm(
        'Deactivate this manufacturer? It will be hidden from the complaint form. Existing complaints are unaffected and you can reactivate it later.'
      )
    ) {
      return;
    }
    setRowBusyId(item.manufacturerId);
    setError(null);
    try {
      const response = await setAdminManufacturerActive(item.manufacturerId, next);
      if (response.status && response.data) {
        setManufacturers(prev =>
          prev.map(item => (item.manufacturerId === item.manufacturerId ? response.data : item))
        );
      }
    } catch (err) {
      setError(
        extractApiErrorMessage(err, `Failed to ${next ? 'reactivate' : 'deactivate'} manufacturer`)
      );
    } finally {
      setRowBusyId(null);
    }
  };

  /** Permanent. The server refuses (409) when any complaint references the manufacturer. */
  const handleDelete = async (item: ManufacturerItem) => {
    if (
      !window.confirm(
        `Permanently delete "${item.name}"?\n\nThis cannot be undone. If any complaint references this manufacturer the delete is refused — deactivate it instead.`
      )
    ) {
      return;
    }
    setRowBusyId(item.manufacturerId);
    setError(null);
    try {
      const response = await deleteAdminManufacturer(item.manufacturerId);
      if (response.status) {
        setManufacturers(prev => prev.filter(item => item.manufacturerId !== item.manufacturerId));
        if (selection.isSelected(item.manufacturerId)) selection.toggle(item.manufacturerId);
      }
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to delete manufacturer'));
    } finally {
      setRowBusyId(null);
    }
  };

  const handleExport = useCallback(async () => {
    try {
      setExporting(true);
      setError(null);
      const blob = await exportAdminManufacturers(); // active items only
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `manufacturers-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export manufacturers');
    } finally {
      setExporting(false);
    }
  }, []);

  const handleImport = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        setImporting(true);
        setError(null);
        setImportResult(null);
        const res = await importAdminManufacturers(file);
        if (res.status && res.data) {
          setImportResult(res.data);
          fetchManufacturers();
        } else {
          setError('Import failed');
        }
      } catch (err) {
        setError(extractApiErrorMessage(err, 'Failed to import manufacturers'));
      } finally {
        setImporting(false);
        e.target.value = '';
      }
    },
    [fetchManufacturers]
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Manufacturer Catalog</h2>
            <p className="text-sm text-gray-500">
              Manage brand-level manufacturers for the complaint form
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={fetchManufacturers}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Export active items to Excel"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Export
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Import from Excel"
            >
              {importing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Import
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleImport}
              className="hidden"
            />
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Manufacturer
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {importResult && (
        <div className="mx-4 mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          <p>
            Imported {importResult.created} of {importResult.total} manufacturers.
            {(importResult.skipped ?? 0) > 0 && (
              <span className="text-gray-600"> ({importResult.skipped ?? 0} already existed)</span>
            )}
          </p>
          {importResult.errors && importResult.errors.length > 0 && (
            <p className="mt-1 text-amber-700">
              {importResult.errors.length} row(s) failed:{' '}
              {importResult.errors.map(e => 'Row ' + e.row + ': ' + e.message).join('; ')}
            </p>
          )}
        </div>
      )}

      <div className="p-4">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : manufacturers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Factory className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No manufacturers yet</p>
            <p className="text-sm mt-1">Add manufacturers to show in the complaint form dropdown</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="w-4 h-4" />
              Add Manufacturer
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <CatalogToolbar
              search={search}
              onSearchChange={setSearch}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              visibleCount={filtered.length}
              totalCount={manufacturers.length}
              searchPlaceholder="Search manufacturers by name…"
            />
            <BulkActionBar
              selectedCount={selection.count}
              allVisibleSelected={allVisibleSelected}
              visibleCount={filtered.length}
              onToggleAllVisible={() => selection.toggleAll(visibleIds)}
              onAction={bulk.runAction}
              onClear={selection.clear}
              busyAction={bulk.busyAction}
              entityLabel="manufacturer"
            />
            {bulk.result && (
              <BulkResultNotice
                result={bulk.result}
                entityLabel="manufacturer"
                onDismiss={bulk.dismissResult}
              />
            )}
            {filtered.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">
                No manufacturers match your search or filter.
              </p>
            ) : (
              <div className="space-y-2">
                {filtered.map(item => {
                  const isActive = item.isActive !== false;
                  const checked = selection.isSelected(item.manufacturerId);
                  const busy = rowBusyId === item.manufacturerId || bulk.busyAction !== null;
                  return (
                    <div
                      key={item.manufacturerId}
                      data-testid="catalog-row"
                      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border rounded-lg ${
                        checked
                          ? 'border-primary-300 bg-primary-50/40'
                          : isActive
                            ? 'border-gray-200 bg-white'
                            : 'border-gray-100 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => selection.toggle(item.manufacturerId)}
                          disabled={bulk.busyAction !== null}
                          className="mt-1 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
                          aria-label={`Select ${item.name}`}
                        />
                        <div className="min-w-0">
                          <p
                            className={`font-medium break-words ${isActive ? 'text-gray-900' : 'text-gray-500'}`}
                          >
                            {item.name}
                            {!isActive && (
                              <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded bg-gray-200 text-gray-600 align-middle">
                                Inactive
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <CatalogRowActions
                        isActive={isActive}
                        onEdit={() => setEditingItem(item)}
                        onToggleActive={() => handleToggleActive(item)}
                        onDelete={() => handleDelete(item)}
                        disabled={busy}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {showCreateModal && (
        <ManufacturerFormModal
          title="Add Manufacturer"
          submitLabel="Add Manufacturer"
          savingLabel="Adding..."
          placeholder="e.g., Acme Foods"
          fallbackErrorMessage="Failed to create manufacturer"
          manufacturersByName={manufacturersByName}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
        />
      )}

      {editingItem && (
        <ManufacturerFormModal
          title="Edit Manufacturer"
          submitLabel="Save"
          savingLabel="Saving..."
          initialName={editingItem.name}
          fallbackErrorMessage="Failed to update manufacturer"
          manufacturersByName={manufacturersByName}
          excludeManufacturerId={editingItem.manufacturerId}
          onClose={() => setEditingItem(null)}
          onSave={name => handleUpdate(editingItem.manufacturerId, name)}
        />
      )}
    </div>
  );
}

/**
 * Single form modal used for both add and edit. Submits the name to the
 * backend; on failure (e.g. duplicate-name 409 from HBAC2-8836) it shows the
 * server's message inline below the input. The backend is the only place
 * that enforces uniqueness — the `manufacturersByName` lookup here is just a
 * UX shortcut so the user sees the collision before clicking.
 */
function ManufacturerFormModal({
  title,
  submitLabel,
  savingLabel,
  placeholder,
  initialName = '',
  fallbackErrorMessage,
  manufacturersByName,
  excludeManufacturerId,
  onClose,
  onSave,
}: {
  title: string;
  submitLabel: string;
  savingLabel: string;
  placeholder?: string;
  initialName?: string;
  fallbackErrorMessage: string;
  manufacturersByName: Map<string, ManufacturerItem>;
  excludeManufacturerId?: string;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // O(1) duplicate lookup on every keystroke. Exclude the item being edited
  // so renaming to a different case/whitespace variant of its own current
  // name doesn't false-trigger (matches updateBrandManufacturer on the BE).
  const normalized = name.trim().toLowerCase();
  const candidate = normalized ? manufacturersByName.get(normalized) : undefined;
  const duplicate =
    candidate && candidate.manufacturerId !== excludeManufacturerId ? candidate : null;

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (submitError) setSubmitError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || saving || duplicate) return;
    setSaving(true);
    setSubmitError(null);
    try {
      await onSave(trimmed);
    } catch (err) {
      setSubmitError(extractApiErrorMessage(err, fallbackErrorMessage));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Manufacturer Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={handleNameChange}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300"
            required
            autoFocus
            aria-invalid={!!duplicate || !!submitError}
          />
          {duplicate && (
            <p className="mt-2 text-sm text-red-700" role="alert">
              A manufacturer named &ldquo;{duplicate.name}&rdquo; already exists in this brand
              catalog.
            </p>
          )}
          {submitError && !duplicate && (
            <p className="mt-2 text-sm text-red-700" role="alert">
              {submitError}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !name.trim() || !!duplicate}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? savingLabel : submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
